import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, ExternalLink, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";

function CopyBox({ label, value, testId }) {
  const copy = () => { navigator.clipboard?.writeText(value); toast.success(`${label} copiado`); };
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex gap-2">
        <code className="flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs max-h-24 overflow-y-auto" data-testid={`${testId}-value`}>{value}</code>
        <Button variant="outline" size="icon" onClick={copy} aria-label={`Copiar ${label}`} data-testid={`${testId}-copy-btn`}><Copy className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function ChargeBody({ c }) {
  const pix = c.method === "pix";
  return (
    <div className="space-y-4">
      {pix && c.qr_code_base64 && <img src={`data:image/png;base64,${c.qr_code_base64}`} alt="QR Code Pix" className="mx-auto h-52 w-52 rounded-lg bg-white p-2" data-testid="charge-pix-qr" />}
      {pix && c.qr_code && <CopyBox label="Pix copia e cola" value={c.qr_code} testId="charge-pix-code" />}
      {!pix && c.digitable_line && <CopyBox label="Linha digitável" value={c.digitable_line} testId="charge-boleto-line" />}
      {c.ticket_url && (
        <Button asChild variant="outline" className="w-full"><a href={c.ticket_url} target="_blank" rel="noreferrer" data-testid="charge-open-link">{pix ? "Abrir página do Pix" : "Abrir boleto (PDF)"}<ExternalLink className="ml-2 h-4 w-4" /></a></Button>
      )}
      <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="charge-waiting"><Loader2 className="h-3.5 w-3.5 animate-spin" />Aguardando pagamento · vence {fmtDateTime(c.expires_at)}</p>
      {!pix && <p className="text-center text-xs text-muted-foreground">Boleto leva até 2 dias úteis para ser confirmado pelo banco. O acesso é liberado sozinho.</p>}
    </div>
  );
}

function Result({ paid }) {
  const Icon = paid ? CheckCircle2 : XCircle;
  return (
    <div className="space-y-4 py-4 text-center" data-testid={paid ? "charge-paid" : "charge-failed"}>
      <Icon className={`mx-auto h-12 w-12 ${paid ? "text-emerald-500" : "text-red-500"}`} />
      <p className="font-display text-lg font-bold">{paid ? "Pagamento confirmado!" : "Cobrança cancelada ou vencida"}</p>
      <p className="text-sm text-muted-foreground">{paid ? "Seu plano já está liberado." : "Gere uma nova cobrança na tela de planos."}</p>
      {paid && <Button asChild data-testid="charge-go-dashboard-btn"><Link to="/app">Ir para o painel</Link></Button>}
    </div>
  );
}

export function ChargeDialog({ charge, onClose }) {
  const { refresh } = useAuth();
  const [st, setSt] = useState("pending");
  useEffect(() => setSt(charge?.payment_status || "pending"), [charge]);
  useEffect(() => {
    if (!charge || st !== "pending") return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/billing/status/${charge.ref}`);
        setSt(data.payment_status);
        if (data.payment_status === "paid") refresh();
      } catch { /* tenta de novo no próximo ciclo */ }
    }, 5000);
    return () => clearInterval(id);
  }, [charge, st, refresh]);
  if (!charge) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="charge-dialog">
        <DialogHeader>
          <DialogTitle>{charge.method === "pix" ? "Pagar com Pix" : "Boleto gerado"}</DialogTitle>
          <DialogDescription data-testid="charge-amount">Valor: <b>{brl(charge.amount)}</b></DialogDescription>
        </DialogHeader>
        {st === "pending" ? <ChargeBody c={charge} /> : <Result paid={st === "paid"} />}
      </DialogContent>
    </Dialog>
  );
}
